import { Button } from '@mdvip/shared/components/ui/button';
import { Separator } from '@mdvip/shared/components/ui/separator';
import { US_STATES } from '@mdvip/shared/lib/us-states';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { BillingAddressValues } from '@/lib/payments/paymentAccount.types';
import { SelectField, TextField } from './fields';

const formatZip = (value: string) => value.replace(/\D/g, '').slice(0, 5);
const STATE_OPTIONS = US_STATES.map((state) => ({ value: state, label: state }));

interface BillingInformationSectionProps {
  accountKind: 'card' | 'bank';
  savedAddress: BillingAddressValues | null;
  /** Whether the "add a new address" sub-form is open. */
  formOpen: boolean;
  /** Toggles the sub-form open/closed. Closing discards entered values (handled by the parent). */
  onToggleForm: () => void;
}

export function BillingInformationSection({
  accountKind,
  savedAddress,
  formOpen,
  onToggleForm,
}: BillingInformationSectionProps) {
  const { t } = useTranslation('members');
  const nameLabel =
    accountKind === 'card'
      ? t('payments.addAccount.billing.cardHolderName')
      : t('payments.addAccount.billing.accountHolderName');
  // No saved address → the entry form is the only option, shown directly.
  const showForm = !savedAddress || formOpen;

  return (
    <div>
      <Separator className="mt-0 mb-6" />
      <h3 className="mb-3 text-sm font-semibold text-foreground">{t('payments.addAccount.billing.title')}</h3>

      {!savedAddress && (
        <p className="-mt-1 mb-3 text-sm text-muted-foreground">{t('payments.addAccount.billing.noSaved')}</p>
      )}

      {savedAddress && (
        <div className="rounded-lg border bg-muted/40 p-3.5">
          <span className="block text-sm font-medium text-foreground">{savedAddress.name}</span>
          <span className="block text-sm leading-relaxed text-muted-foreground">
            {savedAddress.line1}
            {savedAddress.line2 ? `, ${savedAddress.line2}` : ''}
            <br />
            {`${savedAddress.city}, ${savedAddress.state} ${savedAddress.zip}`}
          </span>
        </div>
      )}

      {savedAddress && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2.5"
          onClick={onToggleForm}
          aria-expanded={formOpen}
        >
          {formOpen ? <Trash2 className="size-3.5" /> : <Plus className="size-3.5" />}
          {formOpen ? t('payments.addAccount.billing.discard') : t('payments.addAccount.billing.addNew')}
        </Button>
      )}

      {showForm && (
        <div className={savedAddress ? 'mt-3 rounded-lg border border-dashed p-3.5' : 'mt-3'}>
          {savedAddress && (
            <div className="mb-3 text-xs font-semibold text-muted-foreground">
              {t('payments.addAccount.billing.newAddress')}
            </div>
          )}
          <div className="space-y-3">
            <TextField name="billing.name" label={nameLabel} />
            <TextField name="billing.line1" label={t('payments.addAccount.billing.address1')} />
            <TextField name="billing.line2" label={t('payments.addAccount.billing.address2')} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,0.8fr)_minmax(0,1fr)]">
              <TextField name="billing.city" label={t('payments.addAccount.billing.city')} />
              <SelectField
                name="billing.state"
                label={t('payments.addAccount.billing.state')}
                options={STATE_OPTIONS}
              />
              <TextField
                name="billing.zip"
                label={t('payments.addAccount.billing.zip')}
                inputMode="numeric"
                format={formatZip}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
